<template>
  <div class="category-management dark" dir="rtl">
    <div class="header">
      <h2 class="title">
        مدیریت
        {{ title }}</h2>
      <button class="btn-create" @click="openCreateModal">
        <svg class="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
        </svg>
        ایجاد جدید
      </button>
    </div>

    <!-- جستجو -->
    <div class="search-box">
      <input
          type="text"
          v-model="searchQuery"
          :placeholder="`جستجوی ${title} `"
          class="search-input"
      />
    </div>

    <!-- درخت دسته‌ها -->
    <div class="tree-container" v-if="!loading">
      <tree-management-item
          v-for="category in filteredCategories"
          :key="category.id"
          :category="category"
          :search-query="searchQuery"
          @toggle="toggleExpand"
          @edit="openEditModal"
          @delete="confirmDelete"
      />
      <div v-if="filteredCategories.length === 0" class="empty-message">
        دسته‌ای یافت نشد.
      </div>
    </div>
    <div v-else class="loading">در حال بارگذاری...</div>

    <!-- مودال ایجاد -->
    <transition name="fade-scale">
      <div v-if="createModalOpen" class="modal-overlay" @click.self="closeCreateModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>ایجاد {{ title }}</h3>
            <button class="close-btn" @click="closeCreateModal">&times;</button>
          </div>
          <div class="modal-body">
            <tree-form
                mode="create"
                :base-path="basePath"
                :categories="processedCategories"
                @submitted="handleCreateSuccess"
                @cancel="closeCreateModal"
            />
          </div>
        </div>
      </div>
    </transition>

    <!-- مودال ویرایش -->
    <transition name="fade-scale">
      <div v-if="editModalOpen" class="modal-overlay" @click.self="closeEditModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>ویرایش {{ title }}</h3>
            <button class="close-btn" @click="closeEditModal">&times;</button>
          </div>
          <div class="modal-body">
            <tree-form
                mode="edit"
                :base-path="basePath"
                :categories="processedCategories"
                :initial-data="editingCategory"
                @submitted="handleEditSuccess"
                @cancel="closeEditModal"
            />
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade-scale">
      <div v-if="deleteConfirmOpen" class="modal-overlay" @click.self="cancelDelete">
        <div class="modal-content confirm-dialog">
          <div class="modal-header">
            <h3>تایید حذف</h3>
            <button class="close-btn" @click="cancelDelete">&times;</button>
          </div>
          <div class="modal-body">
            <p>آیا از حذف {{title}} "{{ deletingCategory.name }}" اطمینان دارید؟</p>
            <div class="confirm-actions">
              <button class="btn-danger" @click="deleteCategory" :disabled="deleting">
                <span v-if="deleting">در حال حذف...</span>
                <span v-else>بله، حذف شود</span>
              </button>
              <button class="btn-secondary" @click="cancelDelete">انصراف</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import TreeManagementItem from './TreeManagementItem.vue';
import TreeForm from './TreeForm.vue';

export default {
  name: 'TreeManagement',
  components: { TreeManagementItem, TreeForm },
  props: {
    basePath: { type: String, default: 'feature' },
    title: { type: String, default: ' ویژگی محصولات' }
  },
  data() {
    return {
      loading: true,
      categories: [], // داده‌های خام از سرور
      processedCategories: [], // درخت پردازش شده
      searchQuery: '',
      createModalOpen: false,
      editModalOpen: false,
      deleteConfirmOpen: false,
      editingCategory: null,
      deletingCategory: null,
      deleting: false,
    };
  },
  computed: {
    filteredCategories() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.processedCategories;
      return this.pruneTree(this.processedCategories, q);
    }
  },
  watch: {
    basePath() {
      this.fetchCategories();
    },
  },

  mounted() {
    this.fetchCategories();
  },
  methods: {
    fetchCategories() {
      this.loading = true;
      this.request({
        url: this.endpoint(`product/${this.basePath}Tree`),
        method: 'get',
        success: (data) => {
          this.categories = data;
          this.processedCategories = this.processCategories(data);
          this.loading = false;
        },
        error: (err) => {
          console.error('Error fetching categories:', err);
          this.loading = false;
        }
      });
    },

    processCategories(cats, level = 0, parent = null) {
      return cats.map(cat => ({
        ...cat,
        level,
        parent,
        expanded: false,
        _visible: true,
        children: cat.children ? this.processCategories(cat.children, level + 1, cat) : []
      }));
    },

    pruneTree(nodes, query) {
      const result = [];
      for (const node of nodes) {
        const nameMatch = node.name.toLowerCase().includes(query);
        let children = [];
        if (node.children && node.children.length) {
          children = this.pruneTree(node.children, query);
        }
        if (nameMatch || children.length) {
          result.push({
            ...node,
            expanded: !!query,
            children
          });
        }
      }
      return result;
    },

    toggleExpand(category) {
      category.expanded = !category.expanded;
    },

    openCreateModal() {
      this.createModalOpen = true;
    },
    closeCreateModal() {
      this.createModalOpen = false;
    },
    handleCreateSuccess(newCategory) {
      this.closeCreateModal();
      this.fetchCategories();
      this.successNotify('دسته با موفقیت ایجاد شد');
    },

    openEditModal(category) {
      this.editingCategory = category;
      this.editModalOpen = true;
    },
    closeEditModal() {
      this.editModalOpen = false;
      this.editingCategory = null;
    },
    handleEditSuccess(updatedCategory) {
      this.closeEditModal();
      this.fetchCategories();
      this.successNotify('دسته با موفقیت ویرایش شد');
    },

    confirmDelete(category) {
      this.deletingCategory = category;
      this.deleteConfirmOpen = true;
    },
    cancelDelete() {
      this.deleteConfirmOpen = false;
      this.deletingCategory = null;
    },
    deleteCategory() {
      if (!this.deletingCategory) return;
      this.deleting = true;
      this.request({
        url: this.endpoint(`product/${this.basePath}/${this.deletingCategory.id}`),
        method: 'delete',
        success: () => {
          this.deleting = false;
          this.deleteConfirmOpen = false;
          this.fetchCategories();
          this.successNotify('دسته با موفقیت حذف شد');
        },
        error: (err) => {
          this.deleting = false;
          console.error('Delete error:', err);
          this.successNotify('خطا در حذف دسته', 'error');
        }
      });
    }
  }
};
</script>

<style scoped>
.category-management {
  background: #1a1a1a;
  color: #e0e0e0;
  min-height: 100vh;
  padding: 2rem;
  font-family: 'iranyekan', 'Vazirmatn', sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.btn-create {
  background: linear-gradient(135deg, #2b6e4f, #1e4b3a);
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: opacity 0.2s;
}
.btn-create:hover {
  opacity: 0.9;
}

.search-box {
  margin-bottom: 1.5rem;
}
.search-input {
  width: 100%;
  padding: 0.8rem 1rem;
  background: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  outline: none;
}
.search-input:focus {
  border-color: #2b6e4f;
}

.tree-container {
  background: #242424;
  border-radius: 12px;
  padding: 1rem;
  max-height: 70vh;
  overflow-y: auto;
}

.empty-message {
  text-align: center;
  color: #888;
  padding: 2rem;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #aaa;
}

/* مودال */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: #2a2a2a;
  border-radius: 16px;
  width: 90%;
  max-width: 550px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5);
  border: 1px solid #444;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #333;
  border-bottom: 1px solid #444;
}
.modal-header h3 {
  margin: 0;
  color: #fff;
}
.close-btn {
  background: none;
  border: none;
  color: #aaa;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
}
.close-btn:hover {
  color: #fff;
}

.modal-body {
  padding: 1.5rem;
}

.confirm-dialog {
  max-width: 400px;
}
.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}
.btn-danger {
  background: #b91c1c;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
}
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-secondary {
  background: #4a5568;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
}

.fade-scale-enter-active, .fade-scale-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-scale-enter, .fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>